/**
 * Monthly inspections — the payloads the inspection functions return, and the plain-language pieces
 * every inspection screen shares.
 *
 * HAND-WRITTEN PAYLOAD TYPES. The generated `src/integrations/supabase/types.ts` knows
 * `get_inspection_board`, `get_contract_inspections`, `record_inspection` and
 * `inspection_checklist`, but they return jsonb, which generates as `Json`. Every shape below is
 * read off those function bodies and is the payload contract; `inspectionApi.ts` casts each result
 * to its shape.
 *
 * THE SERVER DERIVES EVERY STATE. Attendance, late, condition, the board chip, a visit's result,
 * the hold and the release all come back from the functions and are rendered as returned. Nothing in
 * this file works one out; the one client-side rule, a visit's result from its own lines, lives in
 * `inspectionResult.ts` and serves only the screens that read the tables directly.
 *
 * No money text anywhere (PLAN §12).
 */

import { format } from 'date-fns';
import { formatBusinessDate, parseBusinessDay } from '@/lib/format';

// ─── Codes ──────────────────────────────────────────────────────────────────

export type Grade = 'ok' | 'fix_soon' | 'fix_now' | 'fixed' | 'not_needed';
export type OpenGrade = 'fix_soon' | 'fix_now';
export type InspectionKind = 'monthly' | 'recheck';
export type Attendance = 'not_owed' | 'not_yet' | 'presented' | 'held' | 'missed';
export type Condition = 'clean' | 'fix_soon' | 'held';
export type VisitResult = 'passed' | 'passed_fix_soon' | 'held' | 'notes_only';
export type BoardChip = 'held' | 'missed' | 'not_yet' | 'done' | 'not_owed';
export type PhotoSlot = 'front_34' | 'driver_side' | 'rear_34' | 'passenger_side' | 'dashboard' | 'interior';

/** The storage bucket every inspection photo lives in (private; read through signed URLs). */
export const INSPECTION_PHOTO_BUCKET = 'vehicle-inspection-photos';

// ─── inspection_checklist() ─────────────────────────────────────────────────

export interface ChecklistSection {
    key: string;
    label: string;
    position: number;
}

export interface ChecklistLine {
    key: string;
    section: string;
    label: string;
    /** Advice shown beside the line. Nothing grades from it: the inspector decides every grade. */
    guidance: string;
    position: number;
    /** YYYY-MM-DD. A monthly grades every line whose added_on is on or before its day. */
    added_on: string;
}

export interface Checklist {
    sections: ChecklistSection[];
    lines: ChecklistLine[];
}

// ─── get_inspection_board(p_period, p_partner_id) ───────────────────────────

export interface BoardRow {
    contract_id: number;
    driver_id: number | null;
    driver_name: string | null;
    /** As stored (233XXXXXXXXX for most drivers). NULL for a partner caller. */
    driver_phone: string | null;
    /** NULL for a partner caller. */
    has_login: boolean | null;
    /**
     * Whether the contract is IN_PROGRESS today. A row is kept for the rest of the span when the
     * contract owed the round and has since ended (false then), so a row alone is not proof of a
     * contract in progress. Only a true row takes a monthly: record_inspection files one on the
     * contract in progress today, and a re-check on a car with none is a between-contracts visit.
     */
    in_progress: boolean;
    vehicle_id: number;
    plate: string | null;
    make: string | null;
    model: string | null;
    /** NULL before the first round. */
    attendance: Attendance | null;
    /** Set only when attendance is presented. */
    late: boolean | null;
    condition: Condition;
    chip: BoardChip | null;
    /** The round's monthly on this contract (the first in the span). */
    inspection_id: number | null;
    inspected_on: string | null;
    result: Exclude<VisitResult, 'notes_only'> | null;
    open_fix_now: number;
    open_fix_soon: number;
    odometer_km: number | null;
    previous_odometer_km: number | null;
    last_service_km: number | null;
}

export interface HeldWithoutContract {
    vehicle_id: number;
    plate: string | null;
    make: string | null;
    model: string | null;
    open_fix_now: number;
    open_fix_soon: number;
    open_items: number;
    earliest_raised_on: string | null;
}

export interface InspectionBoard {
    /** YYYY-MM-01 of the round shown; NULL before the first round. */
    period: string | null;
    opens_on: string | null;
    closes_on: string | null;
    /** The server's Ghana day. */
    today: string;
    counts: {
        held: number;
        missed: number;
        not_yet: number;
        done: number;
        not_owed: number;
        held_without_contract: number;
    };
    rows: BoardRow[];
    held_without_contract: HeldWithoutContract[];
}

// ─── get_contract_inspections(p_contract_id) ────────────────────────────────

export interface RunPhoto {
    id: number;
    url: string;
    line_id: number;
    inspection_id: number;
}

export interface OpenItem {
    item: string;
    grade: OpenGrade;
    note: string | null;
    line_id: number;
    inspection_id: number;
    raised_on: string;
    times_carried: number;
    /** The photos of every line in the item's open run, oldest first. */
    photos: RunPhoto[];
    /** Admin and partner callers only. */
    raised_before_contract?: boolean;
}

export interface CurrentRound {
    period: string | null;
    opens_on: string | null;
    closes_on: string | null;
    today: string;
    attendance: Attendance | null;
    late: boolean | null;
    condition: Condition | null;
    open_fix_now: number | null;
    open_fix_soon: number | null;
    inspection_id: number | null;
    inspected_on: string | null;
    result: Exclude<VisitResult, 'notes_only'> | null;
    released_inspection_id: number | null;
    released_on: string | null;
    next_period: string | null;
    next_opens_on: string | null;
    next_closes_on: string | null;
}

export interface VisitLine {
    id: number;
    item: string;
    grade: Grade;
    note: string | null;
    previous_grade: Grade | null;
}

export interface VisitPhoto {
    id: number;
    url: string;
    slot: PhotoSlot | null;
    line_id: number | null;
}

export interface Visit {
    id: number;
    /** NULL: a legacy, notes-only row. */
    kind: InspectionKind | null;
    contract_id: number | null;
    between_contracts: boolean;
    inspection_at: string;
    inspected_on: string;
    /** Monthlies only: YYYY-MM-01 of the round the visit counts for. */
    period: string | null;
    late: boolean | null;
    result: VisitResult;
    released: boolean | null;
    odometer_km: number | null;
    previous_odometer_km: number | null;
    odometer_change_km: number | null;
    last_service_km: number | null;
    remarks: string | null;
    inspector_first_name: string | null;
    lines: VisitLine[];
    photos: VisitPhoto[];
}

export interface ContractInspections {
    contract_id: number;
    viewer: 'admin' | 'partner' | 'driver';
    header: {
        vehicle_id: number | null;
        plate: string | null;
        make: string | null;
        model: string | null;
        driver_id: number | null;
        driver_name: string | null;
        roadworthy_expires_on: string | null;
        insurance_expires_on: string | null;
    };
    checklist: Checklist;
    open_items: OpenItem[];
    current: CurrentRound;
    visits: Visit[];
}

// ─── record_inspection(...) ─────────────────────────────────────────────────

export interface SubmittedLine {
    item: string;
    grade: Grade;
    note: string | null;
}

export type SubmittedPhoto = { url: string; slot: PhotoSlot } | { url: string; item: string };

export interface RecordInspectionResult {
    inspection_id: number;
    duplicate: boolean;
    result: Exclude<VisitResult, 'notes_only'>;
    held: boolean;
    released: boolean;
    warnings: Array<{ code: string; odometer_km?: number; previous_odometer_km?: number }>;
}

// ─── Words ──────────────────────────────────────────────────────────────────

export const GRADE_LABEL: Record<Grade, string> = {
    ok: 'OK',
    fix_soon: 'Fix soon',
    fix_now: 'Must fix',
    fixed: 'Fixed',
    not_needed: 'No fix needed',
};

export const RESULT_LABEL: Record<VisitResult, string> = {
    passed: 'Passed',
    passed_fix_soon: 'Passed – fix soon',
    held: 'Held',
    notes_only: 'Notes only',
};

export const CONDITION_LABEL: Record<Condition, string> = {
    clean: 'Clean',
    fix_soon: 'Fix soon',
    held: 'Held',
};

export const CHIP_LABEL: Record<BoardChip, string> = {
    held: 'Held',
    missed: 'Missed',
    not_yet: 'Not yet',
    done: 'Done',
    not_owed: 'Not owed',
};

export const ATTENDANCE_LABEL: Record<Attendance, string> = {
    presented: 'Presented',
    not_yet: 'Not yet',
    held: 'Kept at the yard',
    missed: 'Missed',
    not_owed: 'First inspection next round',
};

export const KIND_LABEL: Record<InspectionKind, string> = {
    monthly: 'Monthly',
    recheck: 'Re-check',
};

/** A legacy row (kind NULL) has no lines, no result and no part in holds or attendance. */
export const LEGACY_NOTE = 'Notes only — recorded before the checklist';

/** The six angle shots, in walk-round order. */
export const SLOT_ORDER: readonly PhotoSlot[] = [
    'front_34',
    'driver_side',
    'rear_34',
    'passenger_side',
    'dashboard',
    'interior',
];

export const SLOT_LABEL: Record<PhotoSlot, string> = {
    front_34: 'Front 3/4, plate showing',
    driver_side: 'Driver side',
    rear_34: 'Rear 3/4, plate showing',
    passenger_side: 'Passenger side',
    dashboard: 'Dashboard, engine running',
    interior: 'Interior',
};

/** The note chips: words about the car, never about the driver (a partner reads every note). */
export const NOTE_CHIPS: readonly string[] = ['worn', 'cracked', 'not working', 'leaking', 'missing', 'dirty'];

/** The label of a checklist key, or the key itself when the checklist does not carry it. */
export function lineLabel(checklist: Checklist | null | undefined, item: string): string {
    return checklist?.lines.find((l) => l.key === item)?.label ?? item;
}

/** "2nd inspection" for a line carried once, and so on; null for a line raised this visit. */
export function carriedLabel(timesCarried: number | null | undefined): string | null {
    if (!timesCarried || timesCarried < 1) return null;
    const n = timesCarried + 1;
    const suffix = n % 100 >= 11 && n % 100 <= 13 ? 'th' : ({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th';
    return `${n}${suffix} inspection`;
}

// ─── Dates (Ghana days as the server sent them) ─────────────────────────────

/** 'Nov 2, 2026' for a YYYY-MM-DD the server sent; '-' when missing. */
export function formatInspectionDay(ymd: string | null | undefined): string {
    return formatBusinessDate(ymd);
}

/** 'Nov 2' for a YYYY-MM-DD; '-' when missing or unparseable. */
export function formatShortDay(ymd: string | null | undefined): string {
    const day = parseBusinessDay(ymd);
    return day ? format(day, 'MMM d') : '-';
}

/** '1–7 Nov' (or 'Nov 25 – Dec 7' across a month end) from the server's window dates. */
export function windowLabel(opensOn: string | null | undefined, closesOn: string | null | undefined): string | null {
    const opens = parseBusinessDay(opensOn);
    const closes = parseBusinessDay(closesOn);
    if (!opens || !closes) return null;
    if (opens.getFullYear() === closes.getFullYear() && opens.getMonth() === closes.getMonth()) {
        return `${format(opens, 'd')}–${format(closes, 'd MMM')}`;
    }
    return `${format(opens, 'MMM d')} – ${format(closes, 'MMM d')}`;
}

/** 'November 2026 round' from a round's YYYY-MM-01. */
export function roundName(period: string | null | undefined): string | null {
    const day = parseBusinessDay(period);
    return day ? `${format(day, 'MMMM yyyy')} round` : null;
}

/**
 * The YYYY-MM-01 `months` away from a round's period. This names a month for the round switcher
 * and nothing more: the server answers with that round's window, or NULL when it is no round.
 */
export function shiftPeriod(period: string, months: number): string {
    const year = Number(period.slice(0, 4));
    const month = Number(period.slice(5, 7)) - 1 + months;
    const y = year + Math.floor(month / 12);
    const m = ((month % 12) + 12) % 12;
    return `${String(y).padStart(4, '0')}-${String(m + 1).padStart(2, '0')}-01`;
}

/** '48,210 km'. */
export function formatKm(km: number | null | undefined): string {
    if (km === null || km === undefined) return '-';
    return `${new Intl.NumberFormat('en-GH').format(km)} km`;
}

/** '+740 km' / '−120 km'; null when either reading is missing. */
export function formatKmChange(change: number | null | undefined): string | null {
    if (change === null || change === undefined) return null;
    const abs = new Intl.NumberFormat('en-GH').format(Math.abs(change));
    return `${change < 0 ? '−' : '+'}${abs} km`;
}

// ─── Reaching a driver ──────────────────────────────────────────────────────

/** profiles.phone as most drivers' is stored: 233 then nine digits. */
const STORED_GHANA_PHONE = /^233\d{9}$/;

export interface PhoneLinks {
    display: string;
    /** `tel:+233…`; null when the stored value is not 233 then nine digits. */
    tel: string | null;
    /** `https://wa.me/233…`; null on the same rule. */
    whatsapp: string | null;
}

/**
 * The call and WhatsApp links for a driver's stored phone (PLAN §7). A value that is not
 * `^233\d{9}$` is shown as stored, with no link: a guessed link that rings a stranger is worse
 * than none.
 */
export function driverPhoneLinks(phone: string | null | undefined): PhoneLinks | null {
    const raw = phone?.trim();
    if (!raw) return null;
    if (!STORED_GHANA_PHONE.test(raw)) return { display: raw, tel: null, whatsapp: null };
    return {
        display: `+233 ${raw.slice(3, 5)} ${raw.slice(5, 8)} ${raw.slice(8)}`,
        tel: `tel:+${raw}`,
        whatsapp: `https://wa.me/${raw}`,
    };
}

// ─── Routes ─────────────────────────────────────────────────────────────────

/** The contract inspection page. Every inspection route sits under `inspections`, so an admin
 *  holding only that key reaches it (the layout's guard reads the first path segment). */
export function contractInspectionPath(contractId: number, visitId?: number | null): string {
    return `/dashboard/inspections/contract/${contractId}${visitId ? `#visit-${visitId}` : ''}`;
}

/**
 * The capture page for a car. `contractId` is only the page's key to read the car's view; the
 * contract a visit is recorded on is derived by record_inspection, never sent.
 */
export function capturePath(vehicleId: number, kind: InspectionKind, contractId?: number | null): string {
    const params = new URLSearchParams();
    if (kind === 'recheck') params.set('kind', 'recheck');
    if (contractId) params.set('contract', String(contractId));
    const qs = params.toString();
    return `/dashboard/inspections/new/${vehicleId}${qs ? `?${qs}` : ''}`;
}

/**
 * Whether a screen offers the monthly for a contract (PLAN §6 step 2), from the server's attendance,
 * whether the car is held now, and the board row's `in_progress`: the contract is in progress today,
 * owed and not yet presented this round — and for a held car only while the round is Not yet, since
 * after the window a re-check is what releases it.
 */
export function offersMonthly(attendance: Attendance | null | undefined, heldNow: boolean, inProgress: boolean): boolean {
    if (!inProgress) return false;
    if (attendance === 'not_yet') return true;
    return attendance === 'missed' && !heldNow;
}

/**
 * Why the capture page does not take a monthly the screens would not offer (`offersMonthly`), in
 * the inspector's words; null when the monthly is offered. A late monthly on a car kept at the yard
 * would turn the driver's round from Kept at the yard into Presented, late.
 */
export function whyNoMonthly(
    attendance: Attendance | null | undefined,
    heldNow: boolean,
    inProgress: boolean,
): { title: string; body: string } | null {
    if (offersMonthly(attendance, heldNow, inProgress)) return null;
    if (!inProgress) {
        return {
            title: 'No contract in progress',
            body: 'This car has no contract in progress today, so it owes no monthly inspection. A re-check records a visit between contracts.',
        };
    }
    if (attendance === 'not_owed') {
        return {
            title: 'First inspection next round',
            body: 'This contract owes no monthly inspection this round; its first is next round. A re-check can still be recorded.',
        };
    }
    if (attendance === 'presented') {
        return { title: 'This round’s monthly is recorded', body: 'This contract has presented the car this round. A re-check can still be recorded.' };
    }
    if (heldNow) {
        return { title: 'This car is held', body: 'Its round’s window has closed, so a re-check releases it, not a monthly inspection.' };
    }
    if (attendance === 'held') {
        return {
            title: 'Kept at the yard this round',
            body: 'The car was held during this round’s window, so this contract owes no monthly inspection this round. A re-check records any further visit.',
        };
    }
    return { title: 'No inspection round today', body: 'Today is in no inspection round, so no monthly inspection can be recorded yet. A re-check can still be recorded.' };
}

/** The capture actions a board row offers (PLAN §6 step 2): a held car gets Re-check, and the monthly only on a row in progress today. */
export function rowActions(row: BoardRow): { monthly: boolean; recheck: boolean } {
    const held = row.chip === 'held';
    return { monthly: offersMonthly(row.attendance, held, row.in_progress), recheck: held };
}

/**
 * The car's contract in progress today, as the board reads it: the newest of the car's rows with
 * `in_progress` (should it carry two), or none — then a monthly is refused and a re-check records a
 * between-contracts visit. A row reading false is a contract that owed the round and has ended.
 */
export function activeRowOf(board: Pick<InspectionBoard, 'rows'> | null | undefined, vehicleId: number): BoardRow | null {
    const rows = board?.rows.filter((r) => r.vehicle_id === vehicleId && r.in_progress) ?? [];
    return rows.sort((a, b) => b.contract_id - a.contract_id)[0] ?? null;
}

/** Whether the board reads a contract as in progress today: every contract in progress today has a row on the current board. */
export function inProgressOnBoard(board: Pick<InspectionBoard, 'rows'> | null | undefined, contractId: number): boolean {
    return !!board?.rows.some((r) => r.contract_id === contractId && r.in_progress);
}

// ─── Photo paths ────────────────────────────────────────────────────────────

/**
 * `inspections/<vehicle_id>/<client_ref>/<name>.jpg` — the folder record_inspection checks every
 * photo against. Postgres prints a uuid in lower case, so the client_ref is lowered here or the
 * prefix check refuses an upper-case one (INSPECTION_PHOTO_NOT_FOUND). The name is a slot key or
 * `<item>-<n>`, both inside the server's `^[A-Za-z0-9._-]+$`.
 */
export function inspectionPhotoPath(vehicleId: number, clientRef: string, name: string): string {
    return `inspections/${vehicleId}/${clientRef.toLowerCase()}/${name}.jpg`;
}

// ─── Refusals ───────────────────────────────────────────────────────────────

export interface InspectionRefusal {
    /** The stable code, 'Access denied', or 'UNKNOWN'. */
    code: string;
    sentence: string;
    /** The checklist keys the server named, when it named any. */
    items: string[];
    /** The error's DETAIL: for INSPECTION_ALREADY_RECORDED, the recorded inspection's id. */
    detail: string | null;
}

const REFUSAL_SENTENCE: Record<string, string> = {
    'Access denied': 'Your account cannot record inspections. Ask a superadmin to give it the Inspections permission.',
    INSPECTION_INVALID_INPUT: 'Part of this inspection was not in the shape the server expects. Reload the page and submit again; your answers are saved.',
    INSPECTION_VEHICLE_NOT_FOUND: 'This car is no longer in the system.',
    INSPECTION_CLIENT_REF_REUSED: 'This draft’s reference already belongs to another inspection. Start again to record this one.',
    INSPECTION_CONTRACT_NOT_ACTIVE: 'This car has no contract in progress today, so it owes no monthly inspection. Record a re-check instead.',
    INSPECTION_NO_ROUND: 'Today is in no inspection round, so no monthly inspection can be recorded. Record a re-check instead.',
    INSPECTION_ALREADY_RECORDED: 'This contract’s monthly inspection for this round is already recorded.',
    INSPECTION_CHECKLIST_INCOMPLETE: 'A monthly inspection answers every checklist line once, and the checklist has changed. Reload the car below; the answers that still fit are kept.',
    INSPECTION_RECHECK_INCOMPLETE: 'The car’s open items changed since this draft was started: a re-check grades every item still open. Reload the car below; the answers that still fit are kept.',
    INSPECTION_NOTE_REQUIRED: 'Fix soon, Must fix and No fix needed each need a note.',
    INSPECTION_NOTHING_TO_CLOSE: 'The car’s open items changed since this draft was started: a line marked Fixed or No fix needed is no longer open. Reload the car below; the answers that still fit are kept.',
    INSPECTION_CLOSURE_REQUIRED: 'The car’s open items changed since this draft was started: a line marked OK is now open, and must say how it closed. Reload the car below; the answers that still fit are kept.',
    INSPECTION_PHOTO_NOT_ALLOWED: 'A photo is attached where it cannot go. Remove it, or change the line’s grade.',
    INSPECTION_PHOTO_NOT_FOUND: 'A photo has not finished uploading. Retry it, then submit again.',
    INSPECTION_PHOTO_REQUIRED: 'Each new Must fix needs its own photo. Take one on every line marked Photo required.',
};

/**
 * Refusals that mean the car (or the checklist) changed after the page read it — another visit
 * closed or opened a line. Reading the car again and keeping the answers that still fit clears
 * them; the page offers that in place.
 */
export const REFUSALS_THE_CAR_CHANGED: ReadonlySet<string> = new Set([
    'INSPECTION_CHECKLIST_INCOMPLETE',
    'INSPECTION_RECHECK_INCOMPLETE',
    'INSPECTION_NOTHING_TO_CLOSE',
    'INSPECTION_CLOSURE_REQUIRED',
]);

/** Codes whose message ends with ': <key>, <key>' naming the lines at fault. */
const CODES_NAMING_ITEMS = new Set([
    'INSPECTION_CHECKLIST_INCOMPLETE',
    'INSPECTION_RECHECK_INCOMPLETE',
    'INSPECTION_NOTE_REQUIRED',
    'INSPECTION_NOTHING_TO_CLOSE',
    'INSPECTION_CLOSURE_REQUIRED',
    'INSPECTION_PHOTO_REQUIRED',
]);

/**
 * Every refusal record_inspection raises, as a plain sentence. The server's message starts with
 * the stable code (`INSPECTION_…: …`); anything else is a failure to reach the server, worded so
 * the inspector knows the draft is safe.
 */
export function inspectionRefusal(
    error: { message?: string | null; details?: string | null } | null | undefined,
): InspectionRefusal {
    const message = (error?.message ?? '').trim();
    const detail = error?.details ? String(error.details) : null;

    if (message === 'Access denied' || message.startsWith('Access denied')) {
        return { code: 'Access denied', sentence: REFUSAL_SENTENCE['Access denied'], items: [], detail };
    }

    const match = message.match(/^(INSPECTION_[A-Z_]+)\b/);
    if (match && REFUSAL_SENTENCE[match[1]]) {
        const code = match[1];
        let items: string[] = [];
        if (CODES_NAMING_ITEMS.has(code)) {
            const tail = message.slice(message.lastIndexOf(': ') + 2);
            items = tail.split(',').map((s) => s.trim()).filter((s) => /^[a-z0-9_]+$/.test(s));
        }
        return { code, sentence: REFUSAL_SENTENCE[code], items, detail };
    }

    // fetch() failing (no signal, a dropped connection) reaches supabase-js as a TypeError whose
    // text differs by browser: Chrome "Failed to fetch", Firefox "NetworkError…", Safari "Load failed".
    const offline = !message || /failed to fetch|networkerror|load failed|network request failed/i.test(message);
    return {
        code: 'UNKNOWN',
        sentence: offline
            ? 'The inspection could not be sent. Check the signal and submit again; your answers are saved.'
            : `The inspection was not recorded: ${message}. Your answers are saved; submit again.`,
        items: [],
        detail,
    };
}
